import { find as _find } from 'lodash-es';
import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GroupIcon from '@mui/icons-material/Group';
import Favorite from '@mui/icons-material/Favorite';
import MaterialLink from './material-link';
import { buildImageUrl } from '../utils/images';
import { useAppContext } from '../contexts/app';
import type { Group } from '../types/Group';

type Props = {
  group: Group;
  className?: string;
  simple?: boolean;
  skipVerification?: boolean;
  [key: string]: unknown;
};

const useStyles = makeStyles()((theme) => ({
  link: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  groupLink: {
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  avatar: {
    height: 20,
    width: 20,
    flexShrink: 0,
    borderRadius: '50%',
    objectFit: 'cover' as const,
    marginRight: theme.spacing(0.5),
  },
  iconWrapper: {
    marginLeft: theme.spacing(0.5),
    display: 'inline-flex',
    alignItems: 'center',
  },
  verifiedIcon: {
    color: theme.palette.primary.main,
    fontSize: 20,
  },
  joinedIcon: {
    color: theme.palette.secondary.dark,
    fontSize: 20,
  },
  avatarMissingIcon: {
    color: theme.palette.text.primary,
    width: 20,
    height: 20,
    flexShrink: 0,
    marginRight: theme.spacing(0.5),
  },
}));

const HostGroup = ({ group, className, simple = false, skipVerification = false, ...rest }: Props) => {
  const { classes, cx } = useStyles();
  const { loggedInUser } = useAppContext();

  const membership = useMemo(() => {
    if (simple || !loggedInUser) {
      return null;
    }
    return _find(loggedInUser.groupMembers, ({ group: { id: groupId } }) => (
      groupId === group.id
    ));
  }, [simple, loggedInUser, group]);

  const joined = membership && membership.role !== 'REQUESTED';

  return (
    <div className={cx(classes.link, className)}>
      {group.image ? (
        <img
          alt={group.name}
          src={buildImageUrl(group.image, '20x20')}
          width={20}
          height={20}
          className={classes.avatar}
        />
      ) : (
        <GroupIcon
          className={classes.avatarMissingIcon}
          fontSize="small"
        />
      )}
      <MaterialLink
        href={`/groups/${group.slug}`}
        className={classes.groupLink}
        {...rest}
      >
        {group.name}
      </MaterialLink>
      {!skipVerification && group.verified && (
        <span
          title="This group has been verified by Everyday Rides"
          className={classes.iconWrapper}
        >
          <VerifiedUserIcon
            className={classes.verifiedIcon}
          />
        </span>
      )}
      {joined && (
        <span
          className={classes.iconWrapper}
          title="You're a member of this group"
        >
          <Favorite className={classes.joinedIcon} />
        </span>
      )}
    </div>
  );
};

export default HostGroup;
